// LLM:METADATA
// :topology: [Domain:StateManagement | Role:DictionaryState | Pattern:Module]
// :relates-to: uses: "api.js", required-by: "All Components"
// :rationale: "Centralized dictionary data management"
// :contract: pre: "Dict action dispatched", post: "Dict state updated", invariant: "Dictionary is never null"
// :decision_cache: "Chose Vuex module for centralized dictionary state [DICT-001]"
// LLM:END

import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_STORE_DICT } from "@/constants/topology"
import logger from "@/services/logger"
import {
  STATE_MUTATION_START,
  STATE_MUTATION_COMPLETE,
  STATE_ACTION_START,
  STATE_ACTION_COMPLETE,
} from "@/constants/events"

// Set topology ID for semantic logging
setTopologyId(TOPOLOGY_STORE_DICT)

export default {
  namespaced: false,
  state: {
    dict: {
      orderStatuses: [],
      orderManagers: [],
      paymentTypes: [],
      deliveryTypes: [],
      accCategories: [],
    },
  },
  getters: {
    getDict: (state) => () => state.dict,
    getOrderStatuses: (state) => () => state.dict.orderStatuses,
    getOrderManagers: (state) => () => state.dict.orderManagers,
    getPaymentTypes: (state) => () => state.dict.paymentTypes,
    getDeliveryTypes: (state) => () => state.dict.deliveryTypes,
    getAccCategories: (state) => () => state.dict.accCategories,
  },
  mutations: {
    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useReports.js", "Orders.vue"
    // :rationale: "Updates order statuses in Vuex store with logging"
    // :contract: pre: "New order statuses array provided", post: "Order statuses state updated", invariant: "Statuses array is never null"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-DICT-001]"
    // LLM:END
    SET_ORDER_STATUSES(state, data) {
      logger.debug(STATE_MUTATION_START, "Setting order statuses", {
        mutation: "SET_ORDER_STATUSES",
        entryCount: data?.length || 0,
      })
      state.dict.orderStatuses = data
      logger.debug(STATE_MUTATION_COMPLETE, "Order statuses set", {
        mutation: "SET_ORDER_STATUSES",
        entryCount: data?.length || 0,
      })
    },

    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useReports.js", "Orders.vue"
    // :rationale: "Updates order managers in Vuex store with logging"
    // :contract: pre: "New order managers array provided", post: "Order managers state updated", invariant: "Managers array is never null"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-DICT-002]"
    // LLM:END
    SET_ORDER_MANAGERS(state, data) {
      logger.debug(STATE_MUTATION_START, "Setting order managers", {
        mutation: "SET_ORDER_MANAGERS",
        entryCount: data?.length || 0,
      })
      state.dict.orderManagers = data
      logger.debug(STATE_MUTATION_COMPLETE, "Order managers set", {
        mutation: "SET_ORDER_MANAGERS",
        entryCount: data?.length || 0,
      })
    },

    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useReports.js", "Orders.vue"
    // :rationale: "Updates payment types in Vuex store with logging"
    // :contract: pre: "New payment types array provided", post: "Payment types state updated", invariant: "Types array is never null"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-DICT-003]"
    // LLM:END
    SET_PAYMENT_TYPES(state, data) {
      logger.debug(STATE_MUTATION_START, "Setting payment types", {
        mutation: "SET_PAYMENT_TYPES",
        entryCount: data?.length || 0,
      })
      state.dict.paymentTypes = data
      logger.debug(STATE_MUTATION_COMPLETE, "Payment types set", {
        mutation: "SET_PAYMENT_TYPES",
        entryCount: data?.length || 0,
      })
    },

    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:StateMutation | Pattern:Store]
    // :relates-to: uses: "logger.js", required-by: "useReports.js", "Orders.vue"
    // :rationale: "Updates delivery types in Vuex store with logging"
    // :contract: pre: "New delivery types array provided", post: "Delivery types state updated", invariant: "Types array is never null"
    // :decision_cache: "Chose Vuex mutations for centralized state management [STATE-DICT-004]"
    // LLM:END
    SET_DELIVERY_TYPES(state, data) {
      logger.debug(STATE_MUTATION_START, "Setting delivery types", {
        mutation: "SET_DELIVERY_TYPES",
        entryCount: data?.length || 0,
      })
      state.dict.deliveryTypes = data
      logger.debug(STATE_MUTATION_COMPLETE, "Delivery types set", {
        mutation: "SET_DELIVERY_TYPES",
        entryCount: data?.length || 0,
      })
    },

    SET_ACC_CATEGORIES(state, data) {
      logger.debug(STATE_MUTATION_START, "Setting account categories", {
        mutation: "SET_ACC_CATEGORIES",
        entryCount: data?.length || 0,
      })
      state.dict.accCategories = data
      logger.debug(STATE_MUTATION_COMPLETE, "Account categories set", {
        mutation: "SET_ACC_CATEGORIES",
        entryCount: data?.length || 0,
      })
    },
  },

  actions: {
    setOrderStatuses({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting order statuses action", {
        action: "setOrderStatuses",
        entryCount: newValue?.length || 0,
      })
      commit("SET_ORDER_STATUSES", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "Order statuses action completed", {
        action: "setOrderStatuses",
        entryCount: newValue?.length || 0,
      })
    },

    setOrderManagers({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting order managers action", {
        action: "setOrderManagers",
        entryCount: newValue?.length || 0,
      })
      commit("SET_ORDER_MANAGERS", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "Order managers action completed", {
        action: "setOrderManagers",
        entryCount: newValue?.length || 0,
      })
    },

    setPaymentTypes({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting payment types action", {
        action: "setPaymentTypes",
        entryCount: newValue?.length || 0,
      })
      commit("SET_PAYMENT_TYPES", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "Payment types action completed", {
        action: "setPaymentTypes",
        entryCount: newValue?.length || 0,
      })
    },

    setDeliveryTypes({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting delivery types action", {
        action: "setDeliveryTypes",
        entryCount: newValue?.length || 0,
      })
      commit("SET_DELIVERY_TYPES", newValue)
      logger.debug(STATE_ACTION_COMPLETE, "Delivery types action completed", {
        action: "setDeliveryTypes",
        entryCount: newValue?.length || 0,
      })
    },

    setAccCategories({ commit }, newValue) {
      logger.debug(STATE_ACTION_START, "Setting account categories action", {
        action: "setAccCategories",
        entryCount: newValue?.length || 0,
      })
      commit("SET_ACC_CATEGORIES", newValue)
      logger.debug(
        STATE_ACTION_COMPLETE,
        "Account categories action completed",
        {
          action: "setAccCategories",
          entryCount: newValue?.length || 0,
        }
      )
    },
  },
}
